import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { useBlocker } from 'react-router-dom';
import { draftSchema, type DraftFormValues } from '../schemas/documentTemplate.schema';
import type { TemplateVersion } from '../types/documentTemplates.types';
import { applyServerFieldErrors, isConflict } from '../utils/formErrors';
import { fetchDocumentTemplate } from '../services/documentTemplates';
import { documentTemplateKeys } from './documentTemplateKeys';
import { useSaveDraftMutation } from './useDraftMutations';

const toFormValues = (draft: TemplateVersion): DraftFormValues => ({
  bodyHtml: draft.bodyHtml,
  changeNote: draft.changeNote ?? '',
});

export function useDraftEditor(templateId: string, draft: TemplateVersion) {
  const form = useForm<DraftFormValues>({
    mode: 'onBlur',
    resolver: zodResolver(draftSchema),
    defaultValues: toFormValues(draft),
  });
  const mutation = useSaveDraftMutation(templateId);
  const queryClient = useQueryClient();
  const [isReloading, setIsReloading] = useState(false);
  const [revision, setRevision] = useState(draft.revision);
  const [hasConflict, setHasConflict] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const { isDirty } = form.formState;

  const blocker = useBlocker(isDirty);

  useEffect(() => {
    if (!isDirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isDirty]);

  const save = form.handleSubmit((values) => {
    setSaveError(null);
    mutation.mutate(
      { ...values, revision },
      {
        onSuccess: (saved) => {
          setHasConflict(false);
          setRevision(saved.revision);
          form.reset(toFormValues(saved));
        },
        onError: (error) => {
          if (isConflict(error)) {
            setHasConflict(true);
            return;
          }
          if (!applyServerFieldErrors(error, form.setError)) {
            setSaveError((error as Error).message);
          }
        },
      },
    );
  });

  const reloadLatest = async () => {
    setIsReloading(true);
    try {
      const latest = await queryClient.fetchQuery({
        queryKey: documentTemplateKeys.detail(templateId),
        queryFn: () => fetchDocumentTemplate(templateId),
        staleTime: 0,
      });
      if (!latest.draft) return;
      setRevision(latest.draft.revision);
      setHasConflict(false);
      form.reset(toFormValues(latest.draft));
    } catch (error) {
      setSaveError((error as Error).message);
    } finally {
      setIsReloading(false);
    }
  };

  return {
    form,
    save,
    isSaving: mutation.isPending,
    isReloading,
    hasConflict,
    isDirty,
    revision,
    saveError,
    reloadLatest,
    blocker,
  };
}
